import * as React from 'react'
import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react'
import { DataPromise, Destination, getDataEmptyState, IGraphArrayPayload } from './AxiosContext'
import FileContext from '../context/FileContext'
import useEntity from '../hooks/useEntity'
import { Message } from '@microsoft/microsoft-graph-types'
import { extractReferenceNumbersFromMessage, FILE_REFERENCE_NUMBER_PROP } from '../helpers/referenceNumberHelper'
import { isOfficeInitialized } from '../helpers/outlookHelper'
import { useTranslation } from 'react-i18next'
import AuthorizationContext from './AuthorizationContext'
import { useExchangeIds } from './ExchangeIdContext'

export const MAX_MESSAGE_SIZE = 36_700_160 //max size or fallback to 35MB.

export type EmailAddressDto = {
    name: string
    address: string
}

export type MessageDto = {
    internetMessageId: string
    conversationId: string
    subject: string
    sender: EmailAddressDto
    recipients: EmailAddressDto[]
}

export const mapEmailUser = (details: Office.EmailAddressDetails | Office.EmailUser): EmailAddressDto => ({
    name: details.displayName,
    address: details.emailAddress?.trim(),
})

export const mapOutlookItem = (item: Office.MessageRead): MessageDto => ({
    internetMessageId: item.internetMessageId,
    conversationId: item.conversationId,
    subject: item.subject,
    sender: mapEmailUser(item.sender),
    recipients: item.to.map(recipient => mapEmailUser(recipient)),
})

export type MessageContextType = {
    outlookMessage: Office.MessageRead | Office.MessageCompose // User selects an e-mail in Outlook
    message: DataPromise<Message> // HAMMOCK sets the Graph API representation
    fileIdMap?: Map<string, string> // If and only if fileIdMap is undefined, the mail has not been processed yet.
    messageRestImmutableId: string //The immutable ID is set. Use this as change trigger.
    maxMessageSize: DataPromise<number> // Mailbox message-size limit in bytes.
}

const MessageContext = createContext<MessageContextType>({
    outlookMessage: undefined,
    message: getDataEmptyState(),
    messageRestImmutableId: undefined,
    fileIdMap: undefined,
    maxMessageSize: undefined,
})

export function MessageProvider(props: PropsWithChildren) {
    const { mailbox } = useContext(AuthorizationContext)
    const { validateReferences } = useContext(FileContext)
    const { t: translate } = useTranslation()

    const [outlookMessage, setOutlookMessage] = useState<Office.MessageRead>()
    const [messageRestImmutableId, setMessageRestImmutableId] = useState<string>()
    const [fileIdMap, setFileIdMap] = useState<Map<string, string>>()

    // Look up the mailbox's configured maximum message size (in bytes) via the Graph
    // getMailTips action. Mail tips report the mailbox's max receive size, which in practice
    // equals the send limit in virtually every tenant. The configured limit rarely (if ever)
    // changes, so the first lookup is cached for the rest of the session; concurrent callers
    // share the same in-flight promise. Resolves to undefined outside Outlook or when the
    // lookup fails — callers should fall back to their own cap.
    const [maxMessageSize] = useEntity<number, IGraphArrayPayload<{ maxMessageSize: number }>>(
        () => ({
            method: 'POST',
            url: `/getMailTips`,
            baseURL: Destination.Email,
            data: {
                EmailAddresses: [mailbox],
                MailTipsOptions: 'maxMessageSize',
            },
            headers: { SuppressErrors: true },
        }),
        isOfficeInitialized() ? false : null,
        response => response?.data?.value?.[0]?.maxMessageSize || MAX_MESSAGE_SIZE,
    )

    const outlookItemId = useMemo(() => outlookMessage?.itemId?.trim() || undefined, [outlookMessage])
    const { getImmutableId, setImmutableId, throttled } = useExchangeIds(mailbox)
    const cachedImmutableId = useMemo(() => getImmutableId(outlookItemId), [getImmutableId, outlookItemId])

    // The Prefer: IdType="ImmutableId" header does not translate the id of a message
    // addressed by its mutable id, so the immutable id must be requested explicitly.
    const [immutableId] = useEntity<string, IGraphArrayPayload<{ sourceId: string; targetId: string }>>(
        (id: string) => ({
            method: 'POST',
            url: `/translateExchangeIds`,
            baseURL: Destination.Email,
            data: {
                inputIds: [id],
                sourceIdType: 'ewsId',
                targetIdType: 'restImmutableEntryId',
            },
        }),
        // Three states, and the difference matters:
        // - null is the framework's "not ready" signal, for an item without a usable itemId yet. An empty string
        //   would slip past useEntity's undefined/null guard and fire translateExchangeIds with inputIds: [''],
        //   which fails; null keeps the entity empty rather than rejected, so the fallback below stays off.
        // - undefined rejects without a request, which is what we want once the id is already cached or Graph is
        //   throttling us: the fallback below then addresses the message by its mutable rest id, exactly as it
        //   already does for the tenants where Graph answers 403.
        // - the id itself translates it.
        !outlookItemId ? null : cachedImmutableId || throttled ? undefined : outlookItemId,
        response => response?.data?.value?.[0]?.targetId,
        undefined,
        undefined,
        [outlookMessage, mailbox],
    )

    // The translation is fixed for the life of the message, so remembering it keeps a user who clicks back and
    // forth through the same mails down to one call each rather than one per selection.
    useEffect(() => {
        if (immutableId.loaded && immutableId.data) {
            setImmutableId(outlookItemId, immutableId.data)
        }
    }, [immutableId.loaded, immutableId.data, outlookItemId])

    const immutableOutlookId = useMemo<string>(
        () => (outlookItemId ? Office.context.mailbox.convertToRestId(outlookItemId, Office.MailboxEnums.RestVersion.v1_0) : undefined),
        [outlookItemId, mailbox],
    )
    const [message] = useEntity<Message>(
        (id: string) => ({
            method: 'GET',
            url: `/messages/${id}`,
            baseURL: Destination.Email,
            params: {
                $select:
                    'id,internetMessageId,conversationId,subject,bodyPreview,webLink,hasAttachments,sender,toRecipients,sentDateTime,receivedDateTime',
                $expand: `singleValueExtendedProperties($filter=id eq '${FILE_REFERENCE_NUMBER_PROP}')`,
            },
            headers: { Prefer: 'IdType="ImmutableId"' },
        }),
        // A cached translation counts as loaded; the fallback addresses the message by its mutable rest id.
        cachedImmutableId ?? (immutableId.loaded ? immutableId.data : immutableId.rejected ? immutableOutlookId || null : null),
    )

    useEffect(() => {
        if (isOfficeInitialized()) {
            setOutlookMessage(Office.context.mailbox.item)

            if (Office.context.mailbox.addHandlerAsync) {
                Office.context.mailbox.addHandlerAsync(
                    Office.EventType.ItemChanged,
                    () => {
                        console.log('MessageProvider:', 'Detected switch to mailbox item', Office.context.mailbox.item?.subject)
                        setOutlookMessage(Office.context.mailbox.item)
                    },
                    result => {
                        if (result.status === Office.AsyncResultStatus.Failed) {
                            console.log(
                                'MessageProvider:',
                                'Error in switch detection to mailbox item:',
                                result.error.code,
                                result.error.name,
                                result.error.message,
                            )
                            throw new Error(`${result.error.code}  ${result.error.name}: ${result.error.message}`)
                        }
                    },
                )
                // Intentionally NO cleanup: Office.context.mailbox.removeHandlerAsync cannot target a
                // single handler — it removes EVERY ItemChanged handler on the mailbox. This provider
                // lives for the app's lifetime, so we register once and never remove.
            } else {
                console.log(
                    'MessageProvider:',
                    translate('base.error.outdated_outlook', {
                        host: Office.context.diagnostics.host,
                        version: Office.context.diagnostics.version,
                        platform: Office.context.diagnostics.platform,
                    }),
                )
                throw new Error(
                    translate('base.error.outdated_outlook', {
                        host: Office.context.diagnostics.host,
                        version: Office.context.diagnostics.version,
                        platform: Office.context.diagnostics.platform,
                    }),
                )
            }
        } else {
            setOutlookMessage(undefined)
        }
    }, [])

    // Clear the previous mail's processed-state the instant the selection changes, so consumers
    // (e.g. the File/Ignore buttons gated on messageRestImmutableId) never act on the prior mail
    // while the new mail's chain is still resolving. Both are repopulated by the effect below.
    useEffect(() => {
        setFileIdMap(undefined)
        setMessageRestImmutableId(undefined)
    }, [outlookMessage])

    useEffect(() => {
        if (!message.loaded) {
            return undefined
        }
        // Guard against out-of-order resolution: if the user switches mail while validateReferences
        // is in flight, the stale promise must not clobber the newer mail's state.
        let active = true
        // Prefer the translated id: in the fallback route message.data.id echoes the mutable format.
        const restImmutableId = immutableId.data ?? message.data?.id
        const references = extractReferenceNumbersFromMessage(message.data)

        if (references) {
            validateReferences(references).then(ids => {
                if (!active) {
                    return
                }
                // Set the pair together so consumers never observe a mismatched id/map.
                setFileIdMap(ids?.size === 0 ? undefined : ids)
                setMessageRestImmutableId(restImmutableId)
            })
        } else {
            setFileIdMap(undefined)
            setMessageRestImmutableId(restImmutableId)
        }

        return () => {
            active = false
        }
    }, [message?.data, message.loaded, immutableId.data])

    return (
        <MessageContext.Provider
            value={{
                outlookMessage,
                message,
                fileIdMap,
                messageRestImmutableId,
                maxMessageSize,
            }}
        >
            {props.children}
        </MessageContext.Provider>
    )
}

export default MessageContext
